import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import { Checkbox } from '@/shared/ui/checkbox';
import { useRequirementForm } from '@/modules/agreements/platform/pages/document-detail-page/hooks/use-requirement-form';
import { PolicySelect } from '@/modules/agreements/shared';
import type { AgreementRequiredAction } from '@/api/schemas/agreements.types';

const ACTION_OPTIONS = [
  { value: 'acknowledgment', label: 'Acknowledgment' },
  { value: 'consent', label: 'Consent' },
  { value: 'signature', label: 'Signature' },
];

/**
 * Configure a requirement against a published version.
 *
 * Acknowledgment and consent are separate actions and are never merged. For a vendor
 * signature agreement the verification policy must be chosen explicitly — see the hook.
 *
 * The submit's outcome is reported as a notification, so there is no inline error block.
 */
export function RequirementForm(props: {
  documentId: string;
  versionId: string;
  audience: 'tenant' | 'customer';
  onCreated: () => void;
}): ReactNode {
  const form = useRequirementForm(props);

  return (
    <div className="space-y-4">
      <Select
        label="Required action"
        options={ACTION_OPTIONS}
        value={form.requiredAction}
        onChange={(event) =>
          form.setRequiredAction(event.target.value as AgreementRequiredAction)
        }
      />
      <PolicySelect
        policy={form.policy}
        explicitRequired={form.explicitRequired}
        policyChosen={form.policyChosen}
        onChange={form.setPolicy}
      />
      <Checkbox
        label="Re-acceptance required when a new version is published"
        checked={form.reacceptanceRequired}
        onChange={(event) => form.setReacceptanceRequired(event.target.checked)}
      />
      <Input
        label="Deadline (optional)"
        type="date"
        value={form.deadlineAt}
        onChange={(event) => form.setDeadlineAt(event.target.value)}
      />
      <Button loading={form.busy} disabled={!form.canSubmit} onClick={() => void form.submit()}>
        Create requirement
      </Button>
    </div>
  );
}
